/* ────────────────────────────────────────────────
   pages/admin/manager/documentos/TiposDocumentosPage.tsx
   Con selector visual de coordenadas de firma
   ──────────────────────────────────────────────── */
import { useState, useRef } from "react";
import useSWR from "swr";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Add, Delete, Edit, UploadFile } from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import api from "../../../../api/axios";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type DocumentType = {
  docTypeId: number;
  nombre: string;
  needsSignature: boolean;
  signaturePage: number | null;
  signatureX: number | null;
  signatureY: number | null;
};

const fetcher = (url: string) => api.get(url).then((r) => r.data);

export default function TiposDocumentosPage() {
  /* ───── tabla ───── */
  const { data = [], isLoading, mutate } = useSWR<DocumentType[]>("/document-types", fetcher);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<DocumentType | null>(null);

  /* ───── estados formulario ───── */
  const [needsSig, setNeedsSig] = useState(false);
  const [page, setPage] = useState<number | null>(null);
  const [coord, setCoord] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const [file, setFile] = useState<File | null>(null);

  /* reset al abrir/cerrar */
  const handleOpen = (row: DocumentType | null) => {
    setEditRow(row);
    setNeedsSig(row?.needsSignature ?? false);
    setPage(row?.signaturePage ?? null);
    setCoord({ x: row?.signatureX ?? null, y: row?.signatureY ?? null });
    setFile(null);
    setOpen(true);
  };

  const handleSave: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      nombre: form.get("nombre") as string,
      needsSignature: needsSig,
      signaturePage: needsSig ? page : null,
      signatureX: needsSig ? coord.x : null,
      signatureY: needsSig ? coord.y : null,
    };
    if (editRow) await api.patch(`/document-types/${editRow.docTypeId}`, payload);
    else await api.post("/document-types", payload);
    setOpen(false);
    mutate();
  };

  const handleDelete = async (id: number) => {
    await api.delete(`/document-types/${id}`);
    mutate();
  };

  /* ───── columnas grid ───── */
  const cols: GridColDef[] = [
    { field: "nombre", headerName: "Nombre", flex: 1 },
    {
      field: "needsSignature",
      headerName: "Firma",
      width: 110,
      valueFormatter: ({ value }) => (value ? "Sí" : "No"),
    },
    { field: "signaturePage", headerName: "Página", width: 100 },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleOpen(row)}>
              <Edit fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton color="error" size="small" onClick={() => handleDelete(row.docTypeId)}>
              <Delete fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  /* ───── click → capturar coordenadas ───── */
  const pageRef = useRef<HTMLDivElement>(null);
  const handlePdfClick: React.MouseEventHandler = (e) => {
    if (!pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const ratio = pageRef.current.offsetWidth / 595; // 595 pt = ancho A4
    const xPt = Math.round(relX / ratio);
    const yPt = Math.round((rect.height - relY) / ratio); // invertir eje Y
    setCoord({ x: xPt, y: yPt });
    if (page == null) setPage(1); // si aún no hay página, asumimos 1
  };

  return (
    <Box p={3}>
      {/* encabezado + botón */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <h2>Tipos de documento</h2>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}>
          Nuevo tipo
        </Button>
      </Stack>

      {/* tabla */}
      {isLoading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={data}
          columns={cols}
          getRowId={(r) => r.docTypeId}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10, 25]}
        />
      )}

      {/* diálogo */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editRow ? "Editar tipo" : "Nuevo tipo"}</DialogTitle>

        <Box component="form" onSubmit={handleSave}>
          <DialogContent dividers>
            {/* nombre */}
            <TextField
              fullWidth
              margin="normal"
              required
              label="Nombre"
              name="nombre"
              defaultValue={editRow?.nombre ?? ""}
            />

            {/* checkbox firma */}
            <FormControlLabel
              control={<Checkbox checked={needsSig} onChange={(_, c) => setNeedsSig(c)} />}
              label="Requiere firma"
            />

            {/* selector visual */}
            {needsSig && (
              <Stack spacing={2} mt={2}>
                {/* input file */}
                <Button component="label" variant="outlined" startIcon={<UploadFile />}>
                  PDF de muestra
                  <input
                    hidden
                    accept="application/pdf"
                    type="file"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </Button>

                {/* preview */}
                {file && (
                  <Box
                    border={1}
                    borderColor="#ccc"
                    borderRadius={1}
                    p={1}
                    maxHeight={600}
                    sx={{ overflow: "auto" }}
                  >
                    <Document file={file}>
                      <Page
                        pageNumber={page ?? 1} // ← usar página seleccionada
                        width={600}
                        onClick={handlePdfClick}
                        inputRef={pageRef}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                    <small>Haz clic donde quieras la firma (coord. A4)</small>
                  </Box>
                )}

                {/* coordenadas resultantes */}
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Página"
                    value={page ?? 1}
                    onChange={(e) => setPage(Number(e.target.value) || 1)}
                    sx={{ maxWidth: 120 }}
                  />
                  <TextField
                    label="X (pt)"
                    value={coord.x ?? ""}
                    InputProps={{ readOnly: true }}
                    sx={{ maxWidth: 120 }}
                  />
                  <TextField
                    label="Y (pt)"
                    value={coord.y ?? ""}
                    InputProps={{ readOnly: true }}
                    sx={{ maxWidth: 120 }}
                  />
                </Stack>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Guardar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
